import * as React from 'react';

import { cn } from '../lib/utils.js';

/**
 * The card surface, as a class list.
 *
 * For elements that have to keep their own tag — a list row is an <li>, and
 * swapping it for Card's <div> would break the list semantics. Exported so
 * those sites share this definition instead of copying it.
 */
const cardSurface = 'rounded-lg border bg-card text-card-foreground shadow-sm';

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Not a Card prop — deliberately rejected at the type level (ENG-1307).
   *
   * `Card` is a bare surface: border, radius, shadow, and NO padding. A string
   * passed here used to be spread onto the `<div>` as the native `title`
   * attribute, so it rendered as a hover tooltip and never as a heading, while
   * the children bypassed CardContent and sat flush against the card's edges.
   * Both failures were silent, because `title` is a legal DOM attribute and
   * TypeScript had nothing to object to. Seven call sites had drifted into it.
   *
   * Compose the heading instead:
   *
   *   <Card>
   *     <CardHeader><CardTitle>Payment info</CardTitle></CardHeader>
   *     <CardContent>…</CardContent>
   *   </Card>
   */
  title?: never;
};

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(cardSurface, className)} {...props} />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      // `pt-0` assumes a CardHeader sits above and has already paid for the
      // top gap. A card with no header puts its content first, and the content
      // then sits flush against the card's edge — `first:pt-6` gives it back.
      className={cn('p-6 pt-0 first:pt-6', className)}
      {...props}
    />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  cardSurface,
};
